import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import { ApiError, type ApiClient } from "../api/client";
import type { LoginRequest, TeamSummary } from "../api/types";
import { deriveReviewerId, clearLegacyReviewerId } from "./reviewer";
import type { Role } from "../api/types";
import { loginErrorMessage } from "./loginErrors";

export const TOKEN_STORAGE_KEY = "vmc.token";
export const ME_STORAGE_KEY = "vmc.me";

export type AuthStatus = "idle" | "loading" | "authed" | "error";

export interface AuthState {
  status: AuthStatus;
  token: string | null;
  team: TeamSummary | null;
  displayName: string | null;
  reviewerId: string | null;
  role: Role | null;
  /** Esri basemap key from GET /api/config, in memory only. "" until the
   *  Worker answers, when it withholds the key (paused), or offline. */
  esriApiKey: string;
  error: string | null;
}

type Action =
  | { type: "loading" }
  | {
      type: "authed";
      token: string;
      team: TeamSummary;
      displayName: string | null;
      reviewerId: string | null;
      esriApiKey: string;
    }
  | { type: "error"; message: string }
  | { type: "idle" };

const INITIAL_STATE: AuthState = {
  status: "idle",
  token: null,
  team: null,
  displayName: null,
  reviewerId: null,
  role: null,
  esriApiKey: "",
  error: null,
};

function reducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case "loading":
      return { ...state, status: "loading", error: null };
    case "authed":
      return {
        status: "authed",
        token: action.token,
        team: action.team,
        displayName: action.displayName,
        reviewerId: action.reviewerId,
        role: action.team.role,
        esriApiKey: action.esriApiKey,
        error: null,
      };
    case "error":
      return { ...state, status: "error", error: action.message };
    case "idle":
      return { ...INITIAL_STATE };
  }
}

export interface AuthContextValue extends AuthState {
  login: (req: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  client: ApiClient;
  children: ReactNode;
}

/** The Esri basemap key, asked for once per sign-in. Never stored: a paused
 *  Atlas hands out none, and the next load then draws the free NSW layers. */
async function fetchEsriKey(client: ApiClient): Promise<string> {
  try {
    return (await client.getConfig()).esriApiKey;
  } catch {
    return ""; // offline, paused, or an old Worker: NSW tiles until the next sign-in
  }
}

export function AuthProvider({ client, children }: AuthProviderProps): JSX.Element {
  // If a token is already stored, start in "loading" so route guards don't bounce to /login
  // before the restore-from-localStorage useEffect has a chance to call /me.
  const initial: AuthState =
    typeof localStorage !== "undefined" && localStorage.getItem(TOKEN_STORAGE_KEY) !== null
      ? { ...INITIAL_STATE, status: "loading" }
      : INITIAL_STATE;
  const [state, dispatch] = useReducer(reducer, initial);

  // Restore from localStorage on mount.
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored === null) return;
    let cancelled = false;
    dispatch({ type: "loading" });
    client
      .me()
      .then(async (me) => {
        if (cancelled) return;
        const reviewerId =
          me.displayName !== null && me.displayName.length > 0
            ? await deriveReviewerId(me.team.slug, me.displayName)
            : null;
        clearLegacyReviewerId();
        localStorage.setItem(
          ME_STORAGE_KEY,
          JSON.stringify({ team: me.team, displayName: me.displayName }),
        );
        const esriApiKey = await fetchEsriKey(client);
        if (cancelled) return;
        dispatch({
          type: "authed",
          token: stored,
          team: me.team,
          displayName: me.displayName,
          reviewerId,
          esriApiKey,
        });
      })
      .catch(async (err) => {
        if (cancelled) return;
        const isNetworkFailure = err instanceof ApiError && err.status === 0;
        if (isNetworkFailure) {
          // Offline (or Worker unreachable): the token may be perfectly valid.
          // Keep it, and hydrate from the cached identity when we have one so
          // the offline PWA renders signed in; a later real 401 still signs out
          // via onUnauthorized.
          const raw = localStorage.getItem(ME_STORAGE_KEY);
          if (raw !== null) {
            try {
              const cached = JSON.parse(raw) as { team: TeamSummary; displayName: string | null };
              const reviewerId =
                cached.displayName !== null && cached.displayName.length > 0
                  ? await deriveReviewerId(cached.team.slug, cached.displayName)
                  : null;
              if (cancelled) return;
              dispatch({
                type: "authed",
                token: stored,
                team: cached.team,
                displayName: cached.displayName,
                reviewerId,
                esriApiKey: "", // no Worker to ask; the NSW layers need no key
              });
              return;
            } catch {
              // fall through: corrupt cache — treat as no cache
            }
          }
          dispatch({ type: "idle" }); // no cached identity: show login, but KEEP the token
          return;
        }
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(ME_STORAGE_KEY);
        dispatch({ type: "idle" });
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const login = useCallback(
    async (req: LoginRequest) => {
      dispatch({ type: "loading" });
      try {
        const res = await client.login(req);
        localStorage.setItem(TOKEN_STORAGE_KEY, res.token);
        localStorage.setItem(
          ME_STORAGE_KEY,
          JSON.stringify({ team: res.team, displayName: res.displayName }),
        );
        const reviewerId =
          res.displayName !== null && res.displayName.length > 0
            ? await deriveReviewerId(res.team.slug, res.displayName)
            : null;
        clearLegacyReviewerId();
        const esriApiKey = await fetchEsriKey(client);
        dispatch({
          type: "authed",
          token: res.token,
          team: res.team,
          displayName: res.displayName,
          reviewerId,
          esriApiKey,
        });
      } catch (err) {
        dispatch({ type: "error", message: loginErrorMessage(err) });
        throw err;
      }
    },
    [client],
  );

  const logout = useCallback(async () => {
    try {
      await client.logout();
    } catch {
      // best-effort — server-side session may already be gone
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(ME_STORAGE_KEY);
    dispatch({ type: "idle" });
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
